import React, { useState } from 'react'
import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import {
  LayoutBand,
  ModalInfoSheet,
  useInfoSheet,
  DialogOverlay,
  useOverlay,
  useContainerWidth,
  Separator,
  Card,
  colors,
  Row,
  Button,
} from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import { useTranslation, Trans } from 'react-i18next'
import { Link } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'
import getIsTWAMode from '../../helpers/appMode_helper'
import openDeviceNotificationSettings from '../../helpers/openDeviceNotificationSettings'
import useNotificationsEnabled from '../../hooks/useNotificationsEnabled'
import useNotificationPrefs from '../../hooks/useNotificationPrefs'
import { NOTIFICATIONS_FEATURE_FLAG } from '../../services/notificationPrefsService'
import PWAHeader from '../Activities/PWAHeader'
import PushNotificationManager from '../../components/PushNotificationManager'
import NotificationCategoryRow from './NotificationCategoryRow'
import NotificationEditPanel from './NotificationEditPanel'
import useExternalLinks from '../../hooks/useExternalLinks'

const subheadingCss = css`
  text-align: left;
  margin-bottom: 16px;
  font-size: 0.9375rem;
  line-height: 1.4;
`

const accountSettingsNoteCss = css`
  font-size: 0.875rem;
  color: ${colors.gray60};
  a {
    text-decoration: none;
  }
`

const disabledBannerTitleCss = css`
  font-weight: 600;
  font-size: 0.9375rem;
  margin-bottom: 4px;
  color: ${colors.gray60};
`

const disabledBannerBodyCss = css`
  font-size: 0.8125rem;
  color: ${colors.gray60};
`

const loadingCss = css`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`

const loadErrorCss = css`
  font-size: 0.9375rem;
  line-height: 1.4;
  color: ${colors.gray60};
  padding: 24px 0;
`

const ACCOUNT_SETTINGS_URL = '/en/identity/settings/notifications'

// Push channels are app-specific (e.g. push_together_app); email/sms are global. Only push requires the
// device-level permission the "notifications turned off" flow guards.
const isPushChannel = (channel) => typeof channel === 'string' && channel.startsWith('push')

export default function Notifications() {
  const { t } = useTranslation()
  const initData = { page_detail: 'Notifications' }
  usePageView(initData)

  const isTWA = getIsTWAMode()
  const notificationsFeatureEnabled = useFeatureFlag(NOTIFICATIONS_FEATURE_FLAG).isOn
  // The NPS-backed screen is TWA-only and flag-gated (the endpoints aren't on prod yet). This single
  // value gates both the data fetch and the render, so they can't drift apart.
  const npsScreenEnabled = isTWA && notificationsFeatureEnabled

  const notificationsEnabled = useNotificationsEnabled()
  // Show the "notifications turned off" alert only when we know notifications are disabled. A null value
  // means the state is unknown (e.g. an older TWA build that doesn't report it) — don't false-alarm then.
  const notificationsBlocked = notificationsEnabled === false

  const { categories, isLoading, isError, updatePref } = useNotificationPrefs({ enabled: npsScreenEnabled })

  const [editingCategoryId, setEditingCategoryId] = useState(null)

  const atWidth = useContainerWidth()
  const isWide = atWidth({ default: false, md: true })

  const infoSheetConfig = useInfoSheet({ initiallyBottomSheet: true })
  const overlay = useOverlay()
  const notificationsDisabledOverlay = useOverlay()
  useExternalLinks()

  const handleEdit = (categoryId) => {
    setEditingCategoryId(categoryId)
    if (isWide) {
      overlay.open()
    } else {
      infoSheetConfig.handleClick()
    }
  }

  // Push toggles are locked (and greyed) while device notifications are off; email/sms stay editable.
  const isChannelLocked = (channel) => isPushChannel(channel) && notificationsBlocked

  const handleToggle = (categoryId, channel, value) => {
    // While the device has notifications off, the push toggle is locked in BOTH directions — tapping it
    // (on→off or off→on) points the user to system settings instead of changing the subscription.
    if (isChannelLocked(channel)) {
      notificationsDisabledOverlay.open()
      return
    }
    updatePref({ categoryId, channel, optedIn: value })
  }

  const editingCategory = categories.find((c) => c.categoryId === editingCategoryId)

  const renderCategories = () => {
    if (isLoading) {
      return (
        <div css={loadingCss}>
          <NoticeLoading size="lg" role="progressbar" aria-label={t('common:loading', 'Loading')} />
        </div>
      )
    }
    if (isError) {
      return (
        <p css={loadErrorCss} role="alert">
          {t('notifications.load-error', "We couldn't load your notification settings. Please try again later.")}
        </p>
      )
    }
    return (
      <>
        {categories.map((category) => (
          <NotificationCategoryRow
            key={category.categoryId}
            title={category.name}
            channels={category.channels}
            onEdit={() => handleEdit(category.categoryId)}
          />
        ))}
      </>
    )
  }

  // Everyone outside the gate falls back to the existing, endpoint-free PushNotificationManager.
  if (!npsScreenEnabled) {
    return (
      <LayoutBand maxWidth="xxs" marginX="md">
        <PWAHeader title={t('nav.notifications', 'Notifications')} showBack />
        <PushNotificationManager />
      </LayoutBand>
    )
  }

  return (
    <LayoutBand maxWidth="xxs" marginX="md">
      <Separator size="lg" />
      <PWAHeader title={t('nav.notifications', 'Notifications')} showBack />
      {notificationsBlocked && (
        <>
          <Separator />
          <Card backgroundColor={colors.gray02} edges="none">
            <p css={disabledBannerTitleCss}>
              {t('notifications.disabled-banner.title', 'This device has notifications turned off')}
            </p>
            <p css={disabledBannerBodyCss}>
              {t(
                'notifications.disabled-banner.body',
                'Get the updates you want. Turn on push notifications from FamilySearch in your device settings.'
              )}
            </p>
            <Separator size="sm" />
            <Row alignX="start">
              <Button emphasis="high" onClick={openDeviceNotificationSettings}>
                {t('notifications.disabled-banner.go-to-settings', 'Go to Settings')}
              </Button>
            </Row>
          </Card>
        </>
      )}
      <Separator />
      <p css={subheadingCss}>
        {t('notifications.subheading', "Choose which FamilySearch communications you'd like to receive.")}
      </p>
      <Separator />
      {renderCategories()}
      <Separator />
      <Card backgroundColor={colors.gray02} edges="none">
        <p css={accountSettingsNoteCss}>
          <Trans
            i18nKey="notifications.footer.text"
            components={{
              1: <Link to={ACCOUNT_SETTINGS_URL} target="_blank" />,
            }}
          >
            {
              'Looking for more FamilySearch notifications? Go to your <1>account settings</1> on the FamilySearch website.'
            }
          </Trans>
        </p>
      </Card>

      {editingCategory && !isWide && (
        <ModalInfoSheet {...infoSheetConfig}>
          <NotificationEditPanel category={editingCategory} onToggle={handleToggle} isChannelLocked={isChannelLocked} />
        </ModalInfoSheet>
      )}

      {editingCategory && isWide && (
        <DialogOverlay {...overlay}>
          <NotificationEditPanel category={editingCategory} onToggle={handleToggle} isChannelLocked={isChannelLocked} />
        </DialogOverlay>
      )}

      <DialogOverlay
        {...notificationsDisabledOverlay}
        disableFullscreen
        nonClosable
        title={t('notifications.disabled-dialog.title', 'Device notifications are turned off')}
        footer={
          <Row alignX="end" gutters="sm">
            <Button emphasis="low" onClick={notificationsDisabledOverlay.close}>
              {t('notifications.disabled-dialog.not-now', 'Not now')}
            </Button>
            <Button
              emphasis="low"
              onClick={() => {
                notificationsDisabledOverlay.close()
                openDeviceNotificationSettings()
              }}
            >
              {t('notifications.disabled-dialog.settings', 'Settings')}
            </Button>
          </Row>
        }
      >
        {t(
          'notifications.disabled-dialog.body',
          'Get the updates you want. Turn on push notifications from FamilySearch in your device settings.'
        )}
      </DialogOverlay>
    </LayoutBand>
  )
}
